import { useState, type ReactElement, type ReactNode } from 'react'
import { ParentSize } from '@visx/responsive'
import { Group } from '@visx/group'
import { scaleLinear, scaleTime } from '@visx/scale'
import { LinePath } from '@visx/shape'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { GridRows } from '@visx/grid'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import { cn } from 'app/lib/utils'
import { VERDICT_LABEL } from './VerdictPanel'
import { FIELD_NAME, STAGE_NAME } from './MovementTable'
import { formatAccuracy } from './stats'
import { formatCost } from './pricing'
import {
  PIPELINE_LABEL,
  focusOptions,
  pickSeries,
  trendPoints,
  trendSeries,
  trendSummary,
  type Focus,
  type Metric,
  type SeriesFilter,
  type TrendPoint,
} from './trends'
import { EXECUTION_LABEL, type Run, type VersionEvent } from './types'
import type { Stage } from './scoring'

const PLOT_HEIGHT = 260
const MARGIN = { right: 24, bottom: 32, left: 56 }
/** Height of one row of release labels above the plot. */
const LABEL_ROW = 16
/** Horizontal offset of a run's candidate marker from its baseline dot. */
const CANDIDATE_OFFSET = 7
const DAY_MS = 86_400_000
const shortDate = (date: Date) => date.toLocaleDateString(undefined, { month: `short`, day: `numeric` })

const METRIC_OPTIONS: SegmentedOption<Metric>[] = [
  { id: `score`, label: `Score`, title: `Share of graded fields each side got right` },
  { id: `cost`, label: `Cost per call`, title: `Average model cost of one call, per side` },
  { id: `latency`, label: `Latency`, title: `Average seconds to process one call, per side` },
]

const RANGES = [
  { id: `all`, label: `All time`, days: null },
  { id: `90`, label: `Latest 90 days`, days: 90 },
  { id: `30`, label: `Latest 30 days`, days: 30 },
  { id: `7`, label: `Latest 7 days`, days: 7 },
]

const OVERALL = `overall`

const FORMAT: Record<Metric, (value: number, items: number) => string> = {
  score: formatAccuracy,
  cost: (value) => formatCost(value),
  latency: (value) => `${value.toFixed(1)}s`,
}

/** Only a verdict that backs a direction gets a color; everything else stays neutral. */
function verdictStroke(point: TrendPoint): string {
  if (point.noiseCheck) return `stroke-[var(--fg-minimal)]`
  if (point.verdict === `improved`) return `stroke-green-600`
  if (point.verdict === `regressed`) return `stroke-red-600`
  return `stroke-[var(--fg-subtle)]`
}

/** A diamond marks a candidate that ran the other pipeline; a circle, the same pipeline. */
function CandidateMarker({
  x,
  y,
  size,
  cross,
  className,
}: {
  x: number
  y: number
  size: number
  cross: boolean
  className: string
}): ReactElement {
  return cross ? (
    <rect
      x={x - size}
      y={y - size}
      width={size * 2}
      height={size * 2}
      transform={`rotate(45 ${x} ${y})`}
      className={className}
      strokeWidth={2}
    />
  ) : (
    <circle cx={x} cy={y} r={size} className={className} strokeWidth={2} />
  )
}

type Hover = { point: TrendPoint; x: number; y: number }

function Chart({
  width,
  points,
  events,
  metric,
  onOpenRun,
}: {
  width: number
  points: TrendPoint[]
  events: VersionEvent[]
  metric: Metric
  onOpenRun: (id: string) => void
}): ReactElement {
  const [hover, setHover] = useState<Hover | null>(null)
  const format = FORMAT[metric]
  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right)
  const innerHeight = PLOT_HEIGHT

  const times = points.map((point) => point.date.getTime())
  const span = Math.max(Math.max(...times) - Math.min(...times), DAY_MS)
  const xScale = scaleTime<number>({
    domain: [new Date(Math.min(...times) - span * 0.05), new Date(Math.max(...times) + span * 0.05)],
    range: [0, innerWidth],
  })
  // Scores sit near the top of a 0-100% scale; cost and latency start at zero.
  const values = points.flatMap((point) => [point.baselineRange.low, point.baselineRange.high, point.candidate])
  const yScale = scaleLinear<number>({
    domain:
      metric === `score`
        ? [Math.max(0, Math.floor((Math.min(...values) - 0.02) * 20) / 20), 1]
        : [0, Math.max(...values) * 1.15 || 1],
    range: [innerHeight, 0],
    nice: metric !== `score`,
  })

  // Release labels sit in a lane above the plot. Each takes the first row where it clears
  // the previous label; releases days apart get as many rows as they need, and the lane
  // grows to fit, so labels never overprint.
  const [start, end] = xScale.domain().map((date) => date.getTime())
  const releases = events
    .filter((event) => {
      const time = new Date(event.date).getTime()
      return time >= start && time <= end
    })
    .map((event) => ({ ...event, x: xScale(new Date(event.date)) }))
    .sort((a, b) => a.x - b.x)
  const rowEnds: number[] = []
  const placed = releases.map((release) => {
    const widthEstimate = release.label.length * 6 + 10
    const row = rowEnds.findIndex((endX) => release.x - widthEstimate / 2 > endX + 6)
    const index = row === -1 ? rowEnds.length : row
    rowEnds[index] = release.x + widthEstimate / 2
    return { ...release, row: index }
  })
  const top = 12 + Math.max(1, rowEnds.length) * LABEL_ROW
  const height = top + innerHeight + MARGIN.bottom

  return (
    <div className="relative">
      <svg width={width} height={height} role="img" aria-label="History chart">
        <Group left={MARGIN.left} top={top}>
          <GridRows scale={yScale} width={innerWidth} numTicks={5} stroke="var(--line-default)" />
          <AxisLeft
            scale={yScale}
            numTicks={5}
            hideAxisLine
            hideTicks
            tickFormat={(value) =>
              metric === `score` ? `${Math.round(Number(value) * 100)}%` : format(Number(value), 0)
            }
            tickLabelProps={() => ({
              className: `fill-[var(--fg-minimal)] text-[11px]`,
              textAnchor: `end`,
              dx: -6,
              dy: 3,
            })}
          />
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={Math.max(2, Math.floor(innerWidth / 110))}
            tickFormat={(value) => shortDate(value as Date)}
            hideTicks
            axisLineClassName="stroke-[var(--line-default)]"
            tickLabelProps={() => ({ className: `fill-[var(--fg-minimal)] text-[11px]`, textAnchor: `middle`, dy: 6 })}
          />

          {placed.map((release) => (
            <g key={`${release.label}-${release.date}`}>
              <line
                x1={release.x}
                x2={release.x}
                y1={-6 - release.row * LABEL_ROW}
                y2={innerHeight}
                className="stroke-[var(--line-strong)]"
                strokeDasharray="3 3"
              />
              <text
                x={release.x}
                y={-10 - release.row * LABEL_ROW}
                textAnchor="middle"
                className="fill-[var(--fg-subtle)] text-[11px]"
              >
                {release.label}
              </text>
            </g>
          ))}

          <LinePath<TrendPoint>
            data={points}
            x={(point) => xScale(point.date)}
            y={(point) => yScale(point.baseline)}
            className="stroke-[var(--fg-default)]"
            strokeWidth={2}
          />

          {points.map((point) => {
            const x = xScale(point.date)
            const yBaseline = yScale(point.baseline)
            const yCandidate = yScale(point.candidate)
            const active = hover?.point.run.id === point.run.id
            return (
              <g key={point.run.id}>
                <line
                  x1={x}
                  x2={x}
                  y1={yScale(point.baselineRange.low)}
                  y2={yScale(point.baselineRange.high)}
                  className="stroke-[var(--fg-minimal)]"
                  strokeWidth={1.5}
                />
                {[point.baselineRange.low, point.baselineRange.high].map((value) => (
                  <line
                    key={value}
                    x1={x - 4}
                    x2={x + 4}
                    y1={yScale(value)}
                    y2={yScale(value)}
                    className="stroke-[var(--fg-minimal)]"
                    strokeWidth={1.5}
                  />
                ))}
                <line
                  x1={x}
                  x2={x + CANDIDATE_OFFSET}
                  y1={yBaseline}
                  y2={yCandidate}
                  className={verdictStroke(point)}
                  strokeWidth={2}
                />
                <circle
                  cx={x}
                  cy={yBaseline}
                  r={active ? 5.5 : 4.5}
                  className="fill-[var(--fg-default)] stroke-[var(--surface)]"
                  strokeWidth={2}
                />
                <CandidateMarker
                  x={x + CANDIDATE_OFFSET}
                  y={yCandidate}
                  size={active ? 5 : 4}
                  cross={point.crossPipeline}
                  className={cn(`fill-[var(--surface)]`, verdictStroke(point))}
                />
                <rect
                  x={x - 14}
                  y={0}
                  width={28}
                  height={innerHeight}
                  fill="transparent"
                  className="cursor-pointer"
                  onMouseEnter={() => setHover({ point, x: x + MARGIN.left, y: Math.min(yBaseline, yCandidate) + top })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => onOpenRun(point.run.id)}
                >
                  <title>{point.title}</title>
                </rect>
              </g>
            )
          })}
        </Group>
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 w-64 -translate-x-1/2 -translate-y-full rounded-lg border border-default bg-surface-raised p-3 text-caption-sm shadow-elevation-4"
          style={{ left: Math.min(Math.max(hover.x, 130), width - 130), top: hover.y - 10 }}
        >
          <div className="font-medium text-strong">{hover.point.title}</div>
          <div className="mt-0.5 text-subtle">
            {shortDate(hover.point.date)} · {hover.point.items} calls
          </div>
          <dl className="mt-2 space-y-0.5 tabular-nums">
            <div className="flex justify-between gap-2">
              <dt className="text-subtle">{hover.point.baselineLabel}</dt>
              <dd className="text-strong">
                {format(hover.point.baseline, hover.point.items)}
                <span className="ml-1 text-minimal">
                  ({format(hover.point.baselineRange.low, hover.point.items)}–
                  {format(hover.point.baselineRange.high, hover.point.items)})
                </span>
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-subtle">{hover.point.candidateLabel}</dt>
              <dd className="text-strong">{format(hover.point.candidate, hover.point.items)}</dd>
            </div>
          </dl>
          <div className="mt-2 border-t border-subtle pt-1.5 text-default">
            {hover.point.noiseCheck
              ? `Noise check`
              : hover.point.verdict
                ? VERDICT_LABEL[hover.point.verdict]
                : `No verdict on cost or speed`}
            {` · click to open`}
          </div>
        </div>
      )}
    </div>
  )
}

function LegendItem({ icon, children }: { icon: ReactNode; children: ReactNode }): ReactElement {
  return (
    <li className="flex items-center gap-1.5">
      <svg width="18" height="12" aria-hidden>
        {icon}
      </svg>
      {children}
    </li>
  )
}

const RING = (stroke: string) => (
  <circle cx="9" cy="6" r="4" className={`fill-[var(--surface)] ${stroke}`} strokeWidth="2" />
)

function Legend({ metric, crossLabel }: { metric: Metric; crossLabel: string | null }): ReactElement {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-caption-sm text-subtle">
      <LegendItem
        icon={
          <>
            <line x1="9" x2="9" y1="1" y2="11" className="stroke-[var(--fg-minimal)]" strokeWidth="1.5" />
            <circle cx="9" cy="6" r="3.5" className="fill-[var(--fg-default)]" />
          </>
        }
      >
        Baseline with 95% range
      </LegendItem>
      {metric === `score` ? (
        <>
          <LegendItem icon={RING(`stroke-green-600`)}>Candidate: better</LegendItem>
          <LegendItem icon={RING(`stroke-red-600`)}>worse</LegendItem>
          <LegendItem icon={RING(`stroke-[var(--fg-subtle)]`)}>no demonstrated difference</LegendItem>
        </>
      ) : (
        <LegendItem icon={RING(`stroke-[var(--fg-subtle)]`)}>Candidate</LegendItem>
      )}
      {crossLabel && (
        <LegendItem
          icon={
            <rect
              x="5"
              y="2"
              width="8"
              height="8"
              transform="rotate(45 9 6)"
              className="fill-[var(--surface)] stroke-[var(--fg-subtle)]"
              strokeWidth="2"
            />
          }
        >
          {crossLabel} candidate
        </LegendItem>
      )}
      <LegendItem
        icon={<line x1="9" x2="9" y1="0" y2="12" className="stroke-[var(--line-strong)]" strokeDasharray="3 3" />}
      >
        Prompt release
      </LegendItem>
    </ul>
  )
}

function Picker<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { id: T; label: string }[]
  onChange: (value: T) => void
}): ReactElement {
  return (
    <label className="grid min-w-0 gap-1">
      <span className="text-caption-sm text-subtle">{label}</span>
      <Select value={value} onValueChange={(next) => onChange(next as T)} disabled={options.length < 2}>
        <SelectTrigger className="h-9 w-full" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  )
}

/**
 * History for one comparable series (dataset × execution mode × model × baseline
 * pipeline): each run's baseline with its 95% range, the candidate it tested colored by
 * the run's verdict, and prompt releases. The metric switches between score, cost per
 * call and latency; the focus narrows a score to one stage or field. Click a run to open it.
 */
export function TrendsChart({
  runs,
  events,
  onOpenRun,
}: {
  runs: Run[]
  events: VersionEvent[]
  onOpenRun: (id: string) => void
}): ReactElement {
  const [filter, setFilter] = useState<SeriesFilter>({})
  const [metric, setMetric] = useState<Metric>(`score`)
  const [focusKey, setFocusKey] = useState(OVERALL)
  const [rangeId, setRangeId] = useState(`all`)

  // A window counts back from the newest run, so it never depends on the clock.
  const days = RANGES.find((range) => range.id === rangeId)?.days ?? null
  const newest = Math.max(0, ...runs.map((run) => new Date(run.startedAt).getTime()))
  const inRange =
    days === null ? runs : runs.filter((run) => new Date(run.startedAt).getTime() >= newest - days * DAY_MS)

  const series = trendSeries(inRange)
  const { selected, options } = pickSeries(series, filter)
  const stages = selected ? focusOptions(selected) : []
  const [focusStage, focusField] = focusKey.split(`.`)
  const focusedStage = metric === `score` ? stages.find((entry) => entry.stage === focusStage)?.stage : undefined
  const focus: Focus = focusedStage ? { stage: focusedStage, field: focusField } : {}
  const points = selected ? trendPoints(selected, runs, metric, focus) : []
  const format = FORMAT[metric]
  const summary = trendSummary(points, (value) => format(value, points[0]?.items ?? 0))
  const simulated = selected?.execution === `simulated`
  const crossLabel = points.some((point) => point.crossPipeline)
    ? PIPELINE_LABEL[selected?.pipeline === `staged` ? `single` : `staged`]
    : null
  const datasetName = (id: string) => series.find((entry) => entry.datasetId === id)?.datasetName ?? id
  const set = (patch: SeriesFilter) => setFilter((prev) => ({ ...prev, ...patch }))
  const focusLabel = focus.stage
    ? `${STAGE_NAME[focus.stage as Stage]}${focus.field ? ` · ${FIELD_NAME[focus.field] ?? focus.field}` : ``}`
    : null

  return (
    <section className="mt-6 space-y-4" aria-labelledby="trends-heading">
      <div className="max-w-2xl">
        <h4 id="trends-heading" className="text-base font-semibold text-strong">
          History
        </h4>
        <p className="mt-1 text-sm text-subtle">
          How one dataset scored, run by run. Each run tests a candidate against a baseline, normally the live prompt. A
          rise that stays inside the whiskers is not yet a measurable change.
        </p>
      </div>

      {selected && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Picker
            label="Dataset"
            value={selected.datasetId}
            options={options.datasetId.map((id) => ({ id, label: datasetName(id) }))}
            onChange={(datasetId) => set({ datasetId })}
          />
          <Picker
            label="Mode"
            value={selected.execution}
            options={options.execution.map((id) => ({ id, label: EXECUTION_LABEL[id] }))}
            onChange={(execution) => set({ execution })}
          />
          <Picker
            label="Model"
            value={selected.model}
            options={options.model.map((id) => ({ id, label: id }))}
            onChange={(model) => set({ model })}
          />
          <Picker
            label="Baseline pipeline"
            value={selected.pipeline}
            options={options.pipeline.map((id) => ({ id, label: PIPELINE_LABEL[id] }))}
            onChange={(pipeline) => set({ pipeline })}
          />
          <Picker label="Time" value={rangeId} options={RANGES} onChange={setRangeId} />
        </div>
      )}

      <div className="rounded-xl border border-default bg-surface-raised p-4 sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <SegmentedControl options={METRIC_OPTIONS} value={metric} onSelect={setMetric} className="self-start" />
          {metric === `score` && stages.length > 0 && (
            <Select value={focus.stage ? focusKey : OVERALL} onValueChange={setFocusKey}>
              <SelectTrigger className="h-8 w-full sm:w-64" aria-label="Focus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={OVERALL}>Overall score</SelectItem>
                {stages.map(({ stage, fields }) => (
                  <SelectGroup key={stage}>
                    <SelectLabel>{STAGE_NAME[stage]}</SelectLabel>
                    <SelectItem value={stage}>All {STAGE_NAME[stage]} fields</SelectItem>
                    {fields.map((field) => (
                      <SelectItem key={field} value={`${stage}.${field}`}>
                        {FIELD_NAME[field] ?? field}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {simulated && points.length > 0 && (
          <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-caption-sm text-amber-800">
            Simulated scores: made-up errors, not a real model. The shape of this chart is not evidence.
          </p>
        )}
        {points.length < 2 ? (
          <p className="py-10 text-center text-sm text-subtle">
            {points.length === 1
              ? `One run on ${selected?.datasetName} so far. A history needs at least two runs on the same dataset, mode, model and pipeline.`
              : days === null
                ? `No runs yet.`
                : `No runs in this time range.`}
          </p>
        ) : (
          <>
            <Legend metric={metric} crossLabel={crossLabel} />
            {focusLabel && <p className="mt-2 text-caption-sm text-subtle">Showing {focusLabel} only.</p>}
            <div className="mt-3">
              <ParentSize>
                {({ width }) => (
                  <Chart width={width} points={points} events={events} metric={metric} onOpenRun={onOpenRun} />
                )}
              </ParentSize>
            </div>
            {summary && <p className="mt-2 text-sm text-default">{summary}</p>}
          </>
        )}
      </div>
    </section>
  )
}
